/**
 * Zustand middleware that propagates every `set()` on a mirrored store
 * across all desktop monitor windows so they share canonical state.
 *
 * Owner behaviour:
 *   set() → apply locally → emit `denker://mirror-event { mutation }`
 *   carrying patch + ownerEpoch + monotonic seq + originLabel.
 *
 * Mirror behaviour:
 *   set() → apply locally → emit `denker://mirror-mutation-up` upstream
 *   to the owner. The owner applies it via use-state-owner.ts; its own
 *   middleware re-broadcasts as a normal mutation that all mirrors
 *   (including the originator) receive. The originator drops the echo
 *   in use-state-mirror.ts by matching originLabel === myLabel.
 *
 * See docs/specs/2026-05-04-multimonitor-architecture-reference.md.
 */

import type { StateCreator } from "zustand";
import { emit } from "@tauri-apps/api/event";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { isPinSurface } from "@/desktop/pin/desktop-pin-surface";
import {
  getMyWindowLabel,
  getCurrentOwnerEpoch,
} from "@/lib/owner-state-client";
import { emitMirrorEvent } from "@/lib/mirror-event-emitter";
import { isTauri } from "@/lib/is-tauri";
import { getDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";

// ── Patch derivation ──────────────────────────────────────────────

/**
 * Derive a JSON-serializable partial patch from the before/after states.
 * For non-function partials (the common case) we return the partial itself,
 * stripping non-serializable values (functions, Maps with non-trivial keys).
 * For function partials we diff the top-level keys.
 *
 * Maps are serialized as arrays of [key, value] pairs so the receiving side
 * can reconstruct them.  Only top-level Map fields are handled here; deeply
 * nested Maps are left as-is (the mirror side applies them via Object.assign
 * which may produce incorrect types for nested structures — the frame reducer
 * handles all complex nested mutations via WS frames, not mutations).
 */
function derivePatch<T extends object>(
  prev: T,
  next: T,
  partial: unknown,
  skipNoOps: boolean,
): Record<string, unknown> {
  // Non-function partial — use it directly, but make it serializable.
  if (typeof partial !== "function") {
    return serializePatch(
      partial as Record<string, unknown>,
      skipNoOps,
      prev,
      next,
    );
  }
  // Function updater — diff top-level keys. No-ops are already filtered
  // by the prevVal !== nextVal check below; skipNoOps only applies to the
  // plain-partial branch (serializePatch).
  const patch: Record<string, unknown> = {};
  const keys = new Set([
    ...Object.keys(prev as object),
    ...Object.keys(next as object),
  ]);
  for (const key of keys) {
    const prevVal = (prev as Record<string, unknown>)[key];
    const nextVal = (next as Record<string, unknown>)[key];
    if (prevVal !== nextVal) {
      if (typeof prevVal === "function" || typeof nextVal === "function") {
        continue;
      }
      patch[key] = serializeValue(nextVal);
    }
  }
  return patch;
}

function serializePatch<T extends object>(
  p: Record<string, unknown>,
  skipNoOps: boolean,
  prev?: T,
  next?: T,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) {
    const prevVal = prev ? (prev as Record<string, unknown>)[k] : undefined;
    const nextVal = next ? (next as Record<string, unknown>)[k] : undefined;
    if (
      typeof v === "function" ||
      typeof prevVal === "function" ||
      typeof nextVal === "function"
    ) {
      continue;
    }
    // Skip keys whose value did not actually change. This is what keeps
    // no-op `{cb: null}` callback-slot patches from a mirror from nulling
    // the owner's locally-installed callback, while still letting real
    // value→null clears (e.g. pendingCursorSummon) cross the boundary.
    // Reference comparison is correct here: mirrored stores replace
    // Maps/objects on change rather than mutating in place.
    // NOT applied during a re-broadcast (skipNoOps=false): an authoritative
    // upstream clear must ship to the other mirrors even when the owner's
    // own copy already matched it.
    if (skipNoOps && prev && next && prevVal === nextVal) {
      continue;
    }
    out[k] = serializeValue(v);
  }
  return out;
}

function serializeValue(v: unknown): unknown {
  if (v instanceof Map) {
    return { __map__: true, entries: Array.from(v.entries()) };
  }
  if (v instanceof Set) {
    return { __set__: true, values: Array.from(v.values()) };
  }
  if (typeof v === "function") {
    // Functions cannot cross the Tauri event serialisation boundary. Callers
    // skip function-valued keys before reaching this point so callback slots
    // remain local to each webview.
    return undefined;
  }
  return v;
}

// ── Suppression flag ──────────────────────────────────────────────

/**
 * When true, the middleware skips emitting mutation events.
 * Used to wrap bulk reducer calls (e.g. WS frame application) so
 * the owner emits a single `frame` event instead of N `mutation` events.
 */
let suppressMutationEmit = false;

export function isMutationEmitSuppressed(): boolean {
  return suppressMutationEmit;
}

/** Publish owner-generated toast identities even inside shared frame reduction. */
export function withMutationEmitEnabled<T>(fn: () => T): T {
  const previous = suppressMutationEmit;
  suppressMutationEmit = false;
  try {
    return fn();
  } finally {
    suppressMutationEmit = previous;
  }
}

/**
 * Optional override for the next emit's `originLabel`.
 * Used by use-state-owner when applying a mirror's upstream mutation, so
 * the rebroadcast preserves the originating mirror's label and the
 * originator can drop the echo.
 */
let _overrideOriginLabel: string | null = null;

/**
 * IMPORTANT: fn() must be synchronous. Zustand's setState is sync, so the
 * override window is correct as written. Do NOT await inside fn —
 * _overrideOriginLabel is module-level state and not async-context-safe.
 */
export function withOriginLabel<T>(label: string, fn: () => T): T {
  const prev = _overrideOriginLabel;
  _overrideOriginLabel = label;
  try {
    return fn();
  } finally {
    _overrideOriginLabel = prev;
  }
}

/**
 * Run `fn` with mutation emit suppressed.  The local store `set()` still
 * applies immediately — only the Tauri event emit is suppressed.
 *
 * Use this around any call that applies a WS frame on the owner so that
 * mirrors receive one `frame` event (emitted by the owner connection after
 * the call)
 * rather than N redundant `mutation` events.
 */
export function withMutationEmitSuppressed<T>(fn: () => T): T {
  const prev = suppressMutationEmit;
  suppressMutationEmit = true;
  try {
    return fn();
  } finally {
    suppressMutationEmit = prev;
  }
}

/**
 * Whether a change queued now, for a batch applied later, must stay held
 * back from the owner when it lands. On a mirror, frames are applied with
 * emit suppressed, but a batch flushes on a later animation frame - after
 * the suppression ended - and the mirror would send those frames up as the
 * newest truth, over the owner's. That replaced the owner's conversation
 * and dropped final answers. The owner is unaffected: its frame batches
 * still broadcast, which is how mirrors receive them. (Streamed conversation
 * text never broadcasts: every window derives it from the frames.)
 */
export function holdsDeferredEmit(): boolean {
  return suppressMutationEmit && getWindowRoleSync() === "mirror";
}

/** Stores whose writes stay in this window for now; the others still emit. */
const windowLocalStores = new Set<string>();

/**
 * On a mirror, run `fn` with one store's writes kept in this window while
 * every other store still emits. The canvas store sends its whole node list,
 * so the nodes a mirror places for its own REST loads must not go upstream
 * over the owner's. The owner runs `fn` unchanged.
 */
export function withStoreKeptOnMirror<T>(name: string, fn: () => T): T {
  if (getWindowRoleSync() !== "mirror" || windowLocalStores.has(name)) {
    return fn();
  }
  windowLocalStores.add(name);
  try {
    return fn();
  } finally {
    windowLocalStores.delete(name);
  }
}

// ── Middleware ────────────────────────────────────────────────────

export interface MirroredStoreOptions<T> {
  /**
   * The patch one write sends, from the states before and after it, in
   * place of the changed top-level keys. A keyed store sends only the
   * entries the write touched, so no window's whole copy overwrites another's.
   */
  patchOf?: (prev: T, next: T) => Record<string, unknown>;
}

export function mirroredStore<T extends object>(
  name: string,
  initializer: StateCreator<T, [], []>,
  { patchOf }: MirroredStoreOptions<T> = {},
): StateCreator<T, [], []> {
  return (set, get, api) => {
    const wrappedSet: typeof set = ((
      partialOrUpdater: unknown,
      replace?: boolean,
    ) => {
      const prev = get();
      (set as (p: unknown, r?: boolean) => void)(partialOrUpdater, replace);
      const next = get();

      if (suppressMutationEmit || windowLocalStores.has(name)) return;
      // A pin window is not part of the overlays' mirror ring: what it
      // stores stays its own (its frame comes from the owner's relay).
      if (!isTauri() || isPinSurface()) return;

      const patch = patchOf
        ? patchOf(prev, next)
        : derivePatch(
            prev,
            next,
            partialOrUpdater,
            _overrideOriginLabel === null,
          );
      // Callback closures belong to this webview. Only serializable toast
      // action events cross windows; remote patches cannot own callbacks.
      if (name === "notification") delete patch.systemToastActions;
      if (Object.keys(patch).length === 0) return;

      const role = getWindowRoleSync();
      const myLabel = getMyWindowLabel();

      if (role === "owner") {
        // Owner: broadcast the mutation to all mirrors via the canonical
        // mirror-event channel. originLabel === myLabel by default so if a
        // mirror sent this upstream and we re-broadcast it (using
        // withOriginLabel), the originating mirror can drop the echo.
        emitMirrorEvent({
          kind: "mutation",
          from: myLabel,
          ownerEpoch: getCurrentOwnerEpoch(),
          authScope: getDesktopChatAuthScope(),
          storeName: name,
          patch,
          originLabel: _overrideOriginLabel ?? myLabel,
        });
      } else {
        // Mirror: forward the patch upstream to the owner. The owner will
        // apply it (causing its mirroredStore middleware to re-broadcast as
        // a normal mirror-event mutation that all windows — including this
        // one — receive). originLabel = myLabel so we drop the echo when
        // it returns.
        //
        // Note: real value→null clears MUST be forwarded (dismissing the
        // cursor input on a mirror has to clear the owner's mirrored copy,
        // or the owner is left with stale "bar open" state). No-op null
        // patches are already stripped per-key in serializePatch.
        void emit("denker://mirror-mutation-up", {
          from: myLabel,
          ownerEpoch: getCurrentOwnerEpoch(),
          authScope: getDesktopChatAuthScope(),
          storeName: name,
          patch,
          originLabel: myLabel,
        });
      }
    }) as typeof set;

    // Override api.setState so external `useStore.setState(...)` calls also
    // flow through the emit logic (W5 fix — direct setState bypasses middleware
    // otherwise, leaving mirrors unaware of the mutation).
    api.setState = wrappedSet as typeof api.setState;

    return initializer(wrappedSet, get, api);
  };
}
